import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useWorkspaceStore } from '../stores/workspaceStore'

interface UrnOverride {
  id: string
  connection_id: string
  real_urn: string
  override_urn: string
}

interface UrnOverrideContextType {
  overrides: UrnOverride[]
  loading: boolean
  getEffectiveUrn: (connectionId: string | null | undefined, realUrn: string | undefined) => string | undefined
  getRealUrn: (connectionId: string | null | undefined, effectiveUrn: string | undefined) => string | undefined
  addOverride: (connectionId: string, realUrn: string, overrideUrn: string) => Promise<void>
  removeOverride: (id: string) => Promise<void>
  refreshOverrides: () => Promise<void>
}

const UrnOverrideContext = createContext<UrnOverrideContextType | undefined>(undefined)

export function UrnOverrideProvider({ children }: { children: React.ReactNode }) {
  const { activeWorkspaceId } = useWorkspaceStore()
  const [overrides, setOverrides] = useState<UrnOverride[]>([])
  const [loading, setLoading] = useState(true)

  const refreshOverrides = useCallback(async () => {
    if (!activeWorkspaceId) {
      setOverrides([])
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('urn_overrides')
        .select('*')
        .eq('workspace_id', activeWorkspaceId)
      
      if (error) throw error
      setOverrides(data || [])
    } catch (err) {
      console.error('UrnOverrideProvider: Failed to fetch overrides', err)
    } finally {
      setLoading(false)
    }
  }, [activeWorkspaceId])

  useEffect(() => {
    refreshOverrides()
  }, [refreshOverrides])

  const getEffectiveUrn = useCallback((connectionId: string | null | undefined, realUrn: string | undefined) => {
    if (!connectionId || !realUrn) return realUrn
    const override = overrides.find(o => o.connection_id === connectionId && o.real_urn === realUrn)
    return override ? override.override_urn : realUrn
  }, [overrides])

  const getRealUrn = useCallback((connectionId: string | null | undefined, effectiveUrn: string | undefined) => {
    if (!connectionId || !effectiveUrn) return effectiveUrn
    const override = overrides.find(o => o.connection_id === connectionId && o.override_urn === effectiveUrn)
    return override ? override.real_urn : effectiveUrn
  }, [overrides])

  const addOverride = async (connectionId: string, realUrn: string, overrideUrn: string) => {
    if (!activeWorkspaceId) return

    const { error } = await supabase
      .from('urn_overrides')
      .upsert({
        workspace_id: activeWorkspaceId,
        connection_id: connectionId,
        real_urn: realUrn,
        override_urn: overrideUrn
      })
    
    if (error) throw error
    await refreshOverrides()
  }

  const removeOverride = async (id: string) => {
    const { error } = await supabase
      .from('urn_overrides')
      .delete()
      .eq('id', id)
    
    if (error) throw error
    await refreshOverrides()
  }

  return (
    <UrnOverrideContext.Provider value={{ 
      overrides, 
      loading, 
      getEffectiveUrn, 
      getRealUrn,
      addOverride, 
      removeOverride, 
      refreshOverrides 
    }}>
      {children}
    </UrnOverrideContext.Provider>
  )
}

export function useUrnOverrides() {
  const context = useContext(UrnOverrideContext)
  if (context === undefined) {
    throw new Error('useUrnOverrides must be used within an UrnOverrideProvider')
  }
  return context
}
